import * as React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import SelectServerSchemaContainer from '../containers/SelectServerSchemaContainer';
import DiffViewContainer from '../containers/DiffViewContainer';
import NewDeploymentSummary from './NewDeploymentSummary';
import { useSelectedServer } from '../../../../hooks/useSelectedServer';
import { useSelectedSchema } from '../../../../hooks/useSelectedSchema';
import DeploymentApi from '../../../../api/deploymentApi';
import { useDeploymentList } from '../../../../hooks/useDeploymentList';
import { AppErrorList, useAppErrorList } from '../../../../shared/components/AppErrorList';

interface Props {
  readonly onGoToDeploymentMainView: () => void,
  readonly onGoToDeploymentListView: () => void,
}

const steps = ['Select server and schema', 'Preview changes', 'Start deployment'];

const NewDeploymentSteps = (props: Props) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [selectedServer] = useSelectedServer();
  const [selectedSchema] = useSelectedSchema();
  const [{ errors: deploymentError }] = useDeploymentList();
  const [{ errors, hasErrors }, { addError, setErrors }] = useAppErrorList();

  const serverId = selectedServer?.id;
  const commitId = selectedSchema?.headCommitId;
  const baseCommitId = selectedServer?.lastDeployment?.commitId;

  const isLastStep = () => activeStep === steps.length - 1;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleConfirmDeployment = () => {
    if (serverId && commitId) {
      DeploymentApi.addNewDeployment(serverId, commitId)
        .then(() => {
          props.onGoToDeploymentListView();
        })
        .catch(addError);
    }
  };

  React.useEffect(() => {
    setErrors(deploymentError);
  }, [setErrors, deploymentError]);

  const getStepContent = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        return (
          <SelectServerSchemaContainer />
        );
      case 1:
        return (
          <DiffViewContainer
            baseCommitId={baseCommitId}
            commitId={commitId}
          />
        );
      case 2:
        return (
          <NewDeploymentSummary
            server={selectedServer?.displayName}
            schema={selectedSchema?.name}
            commitId={commitId}
          />
        );
      default:
        return (
          <SelectServerSchemaContainer />
        );
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {hasErrors && (
        <Grid item md={12}>
          <AppErrorList errors={errors} />
        </Grid>
      )}
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label, index) => {
          return (
            <Step key={index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Grid item xs={12}>
        {getStepContent(activeStep)}
      </Grid>

      {activeStep === steps.length ? ('') : (
        <React.Fragment>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              color="inherit"
              disabled={activeStep === 0}
              onClick={isLastStep()
                ? () => props.onGoToDeploymentMainView()
                : handleBack
              }
              style={{ padding: '0 20px' }}
            >
              {isLastStep() ? 'Cancel' : 'Back'}
            </Button>
            <Button
              disabled={!selectedServer || !selectedSchema}
              onClick={isLastStep()
                ? handleConfirmDeployment
                : handleNext}
              style={{ padding: '0 20px' }}
            >
              {isLastStep() ? 'Yes, deploy' : 'Next'}
            </Button>
          </Box>
        </React.Fragment>
      )}
    </Box>
  );
}

export default NewDeploymentSteps;
